import { NavIcon, type NavIconName } from './NavIcon.js';

export interface SidebarItem {
  id: string;
  label: string;
  icon: NavIconName;
  count?: number;
}

export interface SidebarUser {
  initials: string;
  name: string;
  role: string;
}

export interface SidebarProps {
  activeId: string;
  primaryItems: SidebarItem[];
  secondaryItems: SidebarItem[];
  user: SidebarUser;
  brand?: {
    title: string;
    subtitle: string;
    /** When set, the Grow logo image renders instead of the monogram + text. */
    logoSrc?: string;
  };
  onNavigate: (id: string) => void;
}

export function Sidebar({
  activeId,
  primaryItems,
  secondaryItems,
  user,
  brand = { title: 'Grow&Co', subtitle: 'Survey' },
  onNavigate,
}: SidebarProps) {
  return (
    <aside
      style={{
        width: 240,
        flexShrink: 0,
        background: 'var(--bg-elev)',
        borderRight: '1px solid var(--gc-line)',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 18px',
        height: '100vh',
        position: 'sticky',
        top: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '4px 8px 22px',
          borderBottom: '1px solid #F0F2EE',
          marginBottom: 18,
        }}
      >
        {brand.logoSrc ? (
          <img
            src={brand.logoSrc}
            alt={brand.title}
            style={{ width: 150, height: 'auto', objectFit: 'contain', mixBlendMode: 'multiply' }}
          />
        ) : (
          <>
            <div
              aria-hidden="true"
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: 'var(--gc-green-soft)',
                color: 'var(--gc-green-dark)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 18,
              }}
            >
              G&amp;
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 14,
                  color: 'var(--fg)',
                }}
              >
                {brand.title}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 400,
                  fontSize: 10,
                  color: 'var(--gc-stone)',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}
              >
                {brand.subtitle}
              </span>
            </div>
          </>
        )}
      </div>

      <NavGroup
        label="Workspace"
        items={primaryItems}
        activeId={activeId}
        onNavigate={onNavigate}
      />
      {/* Setup / configuration items. Unlabelled: they used to sit under an
          "Insights" heading that stopped describing them once Reports moved into
          the Dashboard. A rule and the gap carry the separation instead. */}
      <div
        style={{
          height: 1,
          background: '#F0F2EE',
          margin: '18px 10px',
        }}
      />
      <NavGroup items={secondaryItems} activeId={activeId} onNavigate={onNavigate} />

      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 8px',
          borderTop: '1px solid #F0F2EE',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--gc-green-dark)',
            color: 'var(--gc-white)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          {user.initials}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 12,
              color: 'var(--fg)',
            }}
          >
            {user.name}
          </span>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--gc-stone)' }}>
            {user.role}
          </span>
        </div>
      </div>
    </aside>
  );
}

function NavGroup({
  label,
  items,
  activeId,
  onNavigate,
}: {
  /** Omit for an unlabelled group — the spacing alone separates it. */
  label?: string;
  items: SidebarItem[];
  activeId: string;
  onNavigate: (id: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {label ? (
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 10,
            letterSpacing: 'var(--track-xwide)',
            textTransform: 'uppercase',
            color: 'var(--gc-stone)',
            padding: '8px 10px 6px',
          }}
        >
          {label}
        </div>
      ) : null}
      {items.map((it) => {
        const isActive = activeId === it.id;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onNavigate(it.id)}
            data-active={isActive ? 'true' : 'false'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              background: isActive ? 'var(--gc-green-soft)' : 'transparent',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'var(--font-sans)',
              fontWeight: isActive ? 600 : 400,
              fontSize: 13,
              color: isActive ? 'var(--gc-green-dark)' : 'var(--gc-charcoal)',
              transition: 'background var(--dur-fast) var(--ease-out)',
              width: '100%',
            }}
          >
            <NavIcon name={it.icon} color={isActive ? 'var(--gc-green-dark)' : 'var(--gc-stone)'} />
            <span style={{ flex: 1 }}>{it.label}</span>
            {it.count != null ? (
              <span
                style={{
                  background: isActive ? 'var(--gc-green-dark)' : 'var(--gc-orange)',
                  color: 'var(--gc-white)',
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '2px 7px',
                  borderRadius: 'var(--radius-pill)',
                  minWidth: 18,
                  textAlign: 'center',
                }}
              >
                {it.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
